import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogDetailPage = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4ECDB]">
        <Header />
        <div className="pt-32 pb-16 px-6 md:px-12">
          <div className="max-w-3xl mx-auto animate-pulse">
            <div className="h-8 bg-[#E8DDC4] rounded w-1/4 mb-4"></div>
            <div className="h-12 bg-[#E8DDC4] rounded w-3/4 mb-8"></div>
            <div className="h-64 bg-[#E8DDC4] rounded mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-[#E8DDC4] rounded w-full"></div>
              <div className="h-4 bg-[#E8DDC4] rounded w-5/6"></div>
              <div className="h-4 bg-[#E8DDC4] rounded w-4/5"></div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-[#F4ECDB]">
        <Header />
        <div className="pt-32 pb-16 px-6 md:px-12 text-center">
          <h1 className="font-serif text-3xl text-[#0F1F18] mb-4">Blog Not Found</h1>
          <Link to="/blog" className="text-[#1F4D34] hover:underline">
            ← Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4ECDB]">
      <Header />
      
      <article className="pt-32 pb-16 px-6 md:px-12" data-testid="blog-detail">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-[#6B7A6E] hover:text-[#1F4D34] mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={18} />
            Back to Blog
          </Link>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-[#6B7A6E] mb-4">
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {formatDate(blog.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <User size={14} />
              {blog.author}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#0F1F18] mb-6">
            {blog.title}
          </h1>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.tags.map((tag, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-[#1F4D34]/10 text-[#1F4D34] text-xs font-medium rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Featured Image */}
          {blog.image_url && (
            <div className="mb-8 rounded-lg overflow-hidden">
              <img 
                src={blog.image_url} 
                alt={blog.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none text-[#0F1F18]"
            style={{ lineHeight: 1.8 }}
          >
            {blog.content.split('\n').map((paragraph, index) => (
              paragraph.trim() && (
                <p key={index} className="mb-4 text-[#6B7A6E]">
                  {paragraph}
                </p>
              )
            ))}
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-[#C9BFA8]">
            <div className="flex items-center justify-between">
              <Link 
                to="/blog" 
                className="text-[#1F4D34] hover:underline"
              >
                ← More Articles
              </Link>
              <button 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: blog.title,
                      url: window.location.href
                    });
                  }
                }}
                className="flex items-center gap-2 text-[#6B7A6E] hover:text-[#1F4D34] transition-colors"
                data-testid="share-btn"
              >
                <Share2 size={18} />
                Share
              </button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
